/**
 * Auth state: who is signed in and which workspace the app should mount.
 * Signed out -> the "local" workspace. Signed in -> the cloud workspace,
 * with a one-time migration of local notes on first sign-in.
 */
import { create } from "zustand";
import { SYNC_TOKEN_TTL_SECONDS } from "@hydra-notes/shared";
import { toast } from "@/ui/toast";

export interface SessionUser {
  id: string;
  email: string;
  displayName: string | null;
  isAdmin?: boolean;
  isPremium?: boolean;
}

interface AuthState {
  status: "loading" | "signedOut" | "signedIn";
  user: SessionUser | null;
  workspaceId: string | null;
  providers: { magicLink: boolean; hepgg: boolean };
  refresh: () => Promise<void>;
  requestMagicLink: (email: string) => Promise<{ ok: boolean; queued?: boolean; error?: string }>;
  signOut: () => Promise<void>;
}

export const useAuth = create<AuthState>((set) => ({
  status: "loading",
  user: null,
  workspaceId: null,
  providers: { magicLink: false, hepgg: false },

  refresh: async () => {
    try {
      const res = await fetch("/api/auth/session", { credentials: "same-origin" });
      if (!res.ok) throw new Error(String(res.status));
      const data = (await res.json()) as {
        user: SessionUser | null;
        workspaceId: string | null;
        providers: { magicLink: boolean; hepgg: boolean };
      };
      set({
        status: data.user ? "signedIn" : "signedOut",
        user: data.user,
        workspaceId: data.workspaceId,
        providers: data.providers,
      });
    } catch {
      // API unreachable (fully offline or server down): stay usable locally.
      set((s) => ({ status: s.user ? "signedIn" : "signedOut" }));
    }
  },

  requestMagicLink: async (email) => {
    try {
      const res = await fetch("/api/auth/magic", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = (await res.json()) as { ok?: boolean; queued?: boolean; error?: string };
      if (!res.ok) return { ok: false, error: data.error ?? "something went wrong" };
      return { ok: true, queued: data.queued };
    } catch {
      return { ok: false, error: "network error, are you offline?" };
    }
  },

  signOut: async () => {
    try {
      await fetch("/api/auth/logout", { method: "POST" });
    } catch {
      // even if the request fails, drop the client session state
    }
    clearSyncTokens();
    set({ status: "signedOut", user: null, workspaceId: null });
    toast.info("Signed out. Notes on this device stay available offline.");
  },
}));

export class SyncTokenError extends Error {
  constructor(
    message: string,
    readonly code: "folder_locked" | "forbidden" | "network",
    readonly spaceId?: string,
  ) {
    super(message);
  }
}

/**
 * Short-lived sync JWTs, one per space, cached until near expiry: a socket
 * reconnect re-auths every attached doc at once, and without the cache that
 * would be one HTTP request per open doc.
 */
const tokenCache = new Map<string, { token: string; expiresAt: number }>();

export function clearSyncTokens(spaceId?: string): void {
  if (spaceId) tokenCache.delete(spaceId);
  else tokenCache.clear();
}

/** Fetch a sync JWT for a space; called by the provider on every (re)connect. */
export async function fetchSyncToken(spaceId?: string): Promise<string> {
  const key = spaceId ?? "";
  const hit = tokenCache.get(key);
  if (hit && Date.now() < hit.expiresAt) return hit.token;
  let res: Response;
  try {
    res = await fetch("/api/auth/sync-token", {
      method: "POST",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(spaceId ? { spaceId } : {}),
    });
  } catch {
    throw new SyncTokenError("sync token unreachable", "network", spaceId);
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    if (res.status === 403 && body.error === "folder_locked") {
      throw new SyncTokenError("folder is over quota for this account", "folder_locked", spaceId);
    }
    throw new SyncTokenError("sync token refused", "forbidden", spaceId);
  }
  const data = (await res.json()) as { token: string };
  // refresh a minute early so an in-flight attach never carries a stale token
  tokenCache.set(key, { token: data.token, expiresAt: Date.now() + (SYNC_TOKEN_TTL_SECONDS - 60) * 1000 });
  return data.token;
}
